import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  LogOut,
  RefreshCw,
  Download,
  Search,
  Mail,
  Building2,
  DollarSign,
  Calendar,
  X,
  Trash2,
  Lock,
} from "lucide-react";
import { ADMIN } from "@/constants/testIds";
import { ASSETS } from "@/data/services";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "tcirl_admin_token";
const TOKEN_EXP_KEY = "tcirl_admin_exp";

const STATUS_OPTIONS = ["new", "contacted", "closed", "archived"];
const STATUS_COLORS = {
  new: "bg-[var(--gold)] text-black",
  contacted: "bg-blue-500/20 text-blue-300 border border-blue-500/40",
  closed: "bg-green-500/20 text-green-300 border border-green-500/40",
  archived: "bg-gray-500/20 text-gray-400 border border-gray-500/40",
};

const TOASTER_STYLE = {
  background: "var(--bg-elevated)",
  color: "var(--text-primary)",
  border: "1px solid var(--border-metal)",
  fontFamily: "Outfit, sans-serif",
};

/* ---------- Token utilities ---------- */
const saveToken = (token, expiresAt) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(TOKEN_EXP_KEY, expiresAt);
};
const clearToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXP_KEY);
};
const readToken = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  const exp = localStorage.getItem(TOKEN_EXP_KEY);
  if (!token || !exp) return null;
  if (new Date(exp) < new Date()) {
    clearToken();
    return null;
  }
  return token;
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

/* ---------- Login screen ---------- */
const AdminLogin = ({ onSuccess }) => {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      saveToken(data.token, data.expires_at);
      onSuccess(data.token);
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute inset-0 metal-texture" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,rgba(212,165,76,0.15)_0%,transparent_70%)] blur-3xl" />

      <div className="relative w-full max-w-md">
        <div className="text-center mb-10">
          <img src={ASSETS.logo} alt="The Closer.irl" className="h-20 w-auto mx-auto mb-6 drop-shadow-[0_10px_30px_rgba(212,165,76,0.3)]" />
          <div className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-[var(--gold)] mb-3">
            <span className="inline-block w-8 h-px bg-[var(--gold)] align-middle mr-3" />
            Admin Dashboard
          </div>
          <h1 className="font-display font-light text-4xl text-[var(--text-primary)]">
            Enter the <span className="italic gold-gradient-text">vault</span>.
          </h1>
        </div>

        <form data-testid={ADMIN.loginForm} onSubmit={submit} className="metal-card p-8 space-y-6">
          <div>
            <label className="lux-label flex items-center gap-2">
              <Lock size={11} /> Password
            </label>
            <input
              data-testid={ADMIN.loginInput}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="lux-input"
              placeholder="••••••••••"
              autoFocus
              required
            />
          </div>

          {error && (
            <div data-testid={ADMIN.loginError} className="text-sm text-red-400 font-mono-accent text-[11px] tracking-[0.15em] uppercase">
              {error}
            </div>
          )}

          <button
            data-testid={ADMIN.loginSubmit}
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-b from-[var(--gold-bright)] to-[var(--gold)] text-black font-mono-accent text-[12px] tracking-[0.3em] uppercase font-bold hover:from-[#ffe5a3] hover:to-[var(--gold-bright)] disabled:opacity-50 transition-all duration-400 gold-glow-hover"
          >
            {submitting ? "Verifying..." : "Sign in"}
          </button>

          <a href="/" className="block text-center font-mono-accent text-[10px] tracking-[0.28em] uppercase text-[var(--text-muted)] hover:text-[var(--gold)] transition">
            ← Back to site
          </a>
        </form>
      </div>
    </div>
  );
};

/* ---------- Dashboard header ---------- */
const AdminHeader = ({ total, onRefresh, onExport, onLogout, loading }) => (
  <header className="border-b border-[var(--border-metal)] bg-black/60 backdrop-blur-xl sticky top-0 z-40">
    <div className="max-w-[1600px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
      <div className="flex items-center gap-4">
        <img src={ASSETS.logo} alt="logo" className="h-11 w-auto" />
        <div className="hidden sm:block leading-tight">
          <div className="font-display text-[15px] font-semibold tracking-wider text-[var(--text-primary)]">HARVER G. LUCIANO</div>
          <div className="font-mono-accent text-[9px] tracking-[0.3em] text-[var(--gold)] uppercase">Admin · Bookings</div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <span data-testid={ADMIN.totalCount} className="hidden md:inline-flex items-center gap-2 font-mono-accent text-[10px] tracking-[0.25em] uppercase text-[var(--text-secondary)] px-3 py-2 border border-[var(--border-metal)]">
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--gold)]" />
          {total} total
        </span>
        <button
          data-testid={ADMIN.refreshBtn}
          onClick={onRefresh}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 border border-[var(--border-metal)] text-[var(--text-secondary)] hover:border-[var(--gold)] hover:text-[var(--gold-bright)] font-mono-accent text-[10px] tracking-[0.25em] uppercase transition-all disabled:opacity-50"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
        <button
          data-testid={ADMIN.exportBtn}
          onClick={onExport}
          className="inline-flex items-center gap-2 px-4 py-2 border border-[var(--gold)] text-[var(--gold-bright)] hover:bg-[var(--gold)] hover:text-black font-mono-accent text-[10px] tracking-[0.25em] uppercase transition-all"
        >
          <Download size={13} /> CSV
        </button>
        <button
          data-testid={ADMIN.logoutBtn}
          onClick={onLogout}
          className="inline-flex items-center gap-2 px-4 py-2 text-[var(--text-muted)] hover:text-red-400 font-mono-accent text-[10px] tracking-[0.25em] uppercase transition-all"
          title="Log out"
        >
          <LogOut size={13} />
        </button>
      </div>
    </div>
  </header>
);

/* ---------- Filters bar ---------- */
const AdminFilters = ({ statusFilter, setStatusFilter, search, setSearch, counts }) => (
  <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
    <div className="flex items-center gap-2 flex-wrap">
      {["all", ...STATUS_OPTIONS].map((s) => (
        <button
          key={s}
          data-testid={`${ADMIN.filterStatus}-${s}`}
          onClick={() => setStatusFilter(s)}
          className={`px-4 py-2 font-mono-accent text-[10px] tracking-[0.25em] uppercase transition-all ${
            statusFilter === s
              ? "bg-[var(--gold)] text-black"
              : "border border-[var(--border-metal)] text-[var(--text-secondary)] hover:border-[var(--gold)] hover:text-[var(--gold-bright)]"
          }`}
        >
          {s} <span className="ml-2 opacity-60">{counts[s] ?? 0}</span>
        </button>
      ))}
    </div>
    <div className="md:ml-auto relative">
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
      <input
        data-testid={ADMIN.search}
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search name, email, message..."
        className="pl-10 pr-4 py-2.5 min-w-[280px] bg-[var(--bg-surface)] border border-[var(--border-metal)] focus:border-[var(--gold)] focus:outline-none text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] font-mono-accent placeholder:font-mono-accent placeholder:text-[10px] placeholder:tracking-[0.15em] placeholder:uppercase transition-colors"
      />
    </div>
  </div>
);

/* ---------- Detail drawer ---------- */
const BookingDetailDrawer = ({ booking, onClose, onDelete }) => {
  if (!booking) return null;
  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <aside
        className="relative ml-auto w-full max-w-2xl h-full bg-[var(--bg-void)] border-l border-[var(--gold)]/40 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-black/80 backdrop-blur-xl border-b border-[var(--border-metal)] px-8 py-5 flex items-center justify-between">
          <div>
            <div className="font-mono-accent text-[9px] tracking-[0.3em] uppercase text-[var(--gold)]">Booking</div>
            <div className="font-mono-accent text-xs text-[var(--text-muted)] mt-1">{booking.id.slice(0, 8)}</div>
          </div>
          <button onClick={onClose} className="p-2 text-[var(--text-muted)] hover:text-[var(--gold-bright)] transition">
            <X size={18} />
          </button>
        </div>

        <div className="px-8 py-8 space-y-6">
          <div>
            <h2 className="font-display font-light text-4xl text-[var(--text-primary)] mb-2">{booking.name}</h2>
            {booking.company && (
              <div className="flex items-center gap-2 text-[var(--text-secondary)] text-sm">
                <Building2 size={14} className="text-[var(--gold)]" /> {booking.company}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <a href={`mailto:${booking.email}?subject=Re: your booking request&body=Hi ${booking.name},%0D%0A%0D%0AThanks for reaching out — `} className="metal-card p-5 group">
              <div className="font-mono-accent text-[9px] tracking-[0.28em] uppercase text-[var(--gold)] mb-2 flex items-center gap-2"><Mail size={11} /> Email · click to reply</div>
              <div className="text-[var(--text-primary)] text-sm break-all group-hover:text-[var(--gold-bright)] transition">{booking.email}</div>
            </a>
            <div className="metal-card p-5">
              <div className="font-mono-accent text-[9px] tracking-[0.28em] uppercase text-[var(--gold)] mb-2 flex items-center gap-2"><Calendar size={11} /> Received</div>
              <div className="text-[var(--text-primary)] text-sm">{formatDate(booking.created_at)}</div>
            </div>
            <div className="metal-card p-5">
              <div className="font-mono-accent text-[9px] tracking-[0.28em] uppercase text-[var(--gold)] mb-2">Service</div>
              <div className="text-[var(--text-primary)] text-sm">{booking.service}</div>
            </div>
            <div className="metal-card p-5">
              <div className="font-mono-accent text-[9px] tracking-[0.28em] uppercase text-[var(--gold)] mb-2 flex items-center gap-2"><DollarSign size={11} /> Budget</div>
              <div className="text-[var(--text-primary)] text-sm">{booking.budget || "—"}</div>
            </div>
          </div>

          <div className="metal-card p-6">
            <div className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-[var(--gold)] mb-3">Message</div>
            <div className="text-[var(--text-secondary)] whitespace-pre-wrap leading-relaxed text-[15px]">{booking.message}</div>
          </div>

          <div className="pt-4 border-t border-[var(--border-metal)]">
            <button
              onClick={() => onDelete(booking.id)}
              className="inline-flex items-center gap-2 text-red-400/70 hover:text-red-400 font-mono-accent text-[10px] tracking-[0.28em] uppercase transition"
            >
              <Trash2 size={12} /> Delete this booking
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
};

/* ---------- Table row ---------- */
const BookingRow = ({ b, onOpen, onStatusChange }) => (
  <tr
    data-testid={ADMIN.row(b.id)}
    className="border-b border-[var(--border-metal)] hover:bg-[var(--bg-surface)]/60 transition cursor-pointer"
    onClick={() => onOpen(b)}
  >
    <td className="px-4 py-4">
      <div className="text-[var(--text-primary)] font-medium text-sm">{b.name}</div>
      <div className="text-[var(--text-muted)] text-xs">{b.company || "—"}</div>
    </td>
    <td className="px-4 py-4">
      <a href={`mailto:${b.email}`} onClick={(e) => e.stopPropagation()} className="text-[var(--gold-bright)] hover:underline text-sm break-all">
        {b.email}
      </a>
    </td>
    <td className="px-4 py-4 text-[var(--text-secondary)] text-sm">{b.service}</td>
    <td className="px-4 py-4 text-[var(--text-muted)] text-sm">{b.budget || "—"}</td>
    <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
      <select
        data-testid={ADMIN.statusSelect(b.id)}
        value={b.status}
        onChange={(e) => onStatusChange(b.id, e.target.value)}
        className={`px-3 py-1.5 font-mono-accent text-[10px] tracking-[0.2em] uppercase cursor-pointer outline-none ${STATUS_COLORS[b.status] || STATUS_COLORS.new}`}
      >
        {STATUS_OPTIONS.map((s) => (
          <option key={s} value={s} className="bg-[var(--bg-surface)] text-[var(--text-primary)]">{s}</option>
        ))}
      </select>
    </td>
    <td className="px-4 py-4 text-[var(--text-muted)] text-xs font-mono-accent whitespace-nowrap">
      {formatDate(b.created_at)}
    </td>
  </tr>
);

/* ---------- Dashboard ---------- */
const AdminDashboard = ({ token, onLogout }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const authHeader = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/bookings`, { headers: authHeader });
      setBookings(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        onLogout();
      } else {
        toast.error("Failed to load bookings.");
      }
    } finally {
      setLoading(false);
    }
  }, [authHeader, onLogout]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/admin/bookings/${id}`, { status }, { headers: authHeader });
      setBookings((prev) => prev.map((b) => (b.id === id ? data : b)));
      setSelected((s) => (s && s.id === id ? data : s));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Failed to update status.");
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Delete this booking permanently?")) return;
    try {
      await axios.delete(`${API}/admin/bookings/${id}`, { headers: authHeader });
      setBookings((prev) => prev.filter((b) => b.id !== id));
      setSelected(null);
      toast.success("Booking deleted.");
    } catch {
      toast.error("Failed to delete.");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/bookings/export.csv`, {
        headers: authHeader,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "thecloser-bookings.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed.");
    }
  };

  const counts = useMemo(() => {
    const c = { all: bookings.length };
    for (const s of STATUS_OPTIONS) c[s] = 0;
    for (const b of bookings) c[b.status] = (c[b.status] || 0) + 1;
    return c;
  }, [bookings]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return bookings.filter((b) => {
      if (statusFilter !== "all" && b.status !== statusFilter) return false;
      if (!q) return true;
      return (
        b.name?.toLowerCase().includes(q) ||
        b.email?.toLowerCase().includes(q) ||
        b.service?.toLowerCase().includes(q) ||
        b.company?.toLowerCase().includes(q) ||
        b.message?.toLowerCase().includes(q)
      );
    });
  }, [bookings, statusFilter, search]);

  return (
    <div data-testid={ADMIN.dashboard} className="min-h-screen bg-[var(--bg-void)]">
      <AdminHeader total={bookings.length} onRefresh={fetchBookings} onExport={exportCsv} onLogout={onLogout} loading={loading} />

      <main className="max-w-[1600px] mx-auto px-6 lg:px-10 py-10">
        <div className="mb-8">
          <div className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-[var(--gold)] mb-2">
            <span className="inline-block w-8 h-px bg-[var(--gold)] align-middle mr-3" />
            Inbox
          </div>
          <h1 className="font-display font-light text-4xl lg:text-5xl text-[var(--text-primary)]">
            All <span className="italic gold-gradient-text">bookings</span>.
          </h1>
        </div>

        <AdminFilters
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          search={search}
          setSearch={setSearch}
          counts={counts}
        />

        <div className="metal-card overflow-hidden">
          {loading && bookings.length === 0 ? (
            <div className="p-20 text-center font-mono-accent text-[11px] tracking-[0.3em] uppercase text-[var(--text-muted)]">
              Loading bookings…
            </div>
          ) : filtered.length === 0 ? (
            <div data-testid={ADMIN.emptyState} className="p-20 text-center">
              <div className="font-display italic text-3xl gold-gradient-text mb-3">Nothing here.</div>
              <p className="font-mono-accent text-[10px] tracking-[0.28em] uppercase text-[var(--text-muted)]">
                {bookings.length === 0 ? "No bookings yet. Share your link to start closing." : "No matches for the current filter."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-black/50 border-b border-[var(--border-metal)]">
                  <tr className="font-mono-accent text-[9px] tracking-[0.28em] uppercase text-[var(--gold)]">
                    <th className="px-4 py-3 text-left">Lead</th>
                    <th className="px-4 py-3 text-left">Email</th>
                    <th className="px-4 py-3 text-left">Service</th>
                    <th className="px-4 py-3 text-left">Budget</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-left">Received</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((b) => (
                    <BookingRow key={b.id} b={b} onOpen={setSelected} onStatusChange={updateStatus} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <BookingDetailDrawer booking={selected} onClose={() => setSelected(null)} onDelete={deleteBooking} />
    </div>
  );
};

/* ---------- Root Admin App ---------- */
const AdminApp = () => {
  const [token, setToken] = useState(() => readToken());

  const handleLogout = () => {
    clearToken();
    setToken(null);
  };

  return (
    <div className="App min-h-screen bg-[var(--bg-void)]">
      <Toaster position="top-right" theme="dark" toastOptions={{ style: TOASTER_STYLE }} />
      {token ? (
        <AdminDashboard token={token} onLogout={handleLogout} />
      ) : (
        <AdminLogin onSuccess={setToken} />
      )}
    </div>
  );
};

export default AdminApp;
